/**
 * hud.ts — DOM overlay HUD.
 *
 * Top bar shows the six v1 resources (UI_UX §1.1) in the canonical order
 * **Credits, Minerals, Fuel, Research, Population, Diplomatic** plus a
 * tick counter. Each resource cell shows `current/cap +income`:
 *
 *   Credits   5,000 / 20,000 +54
 *   Minerals  1,200 / 10,000 +8
 *   Fuel      430 / 10,000 +1
 *   Research  420 +5
 *   Pop       12.3 / 100 +0.02
 *   Diplomatic 8 / 1,000 +1
 *
 * - `current` is the live empire resource.
 * - `cap` is computed per STRUCTURES §2.1 via `resourceCapsForEmpire` for
 *   storage resources, `DIPLOMATIC_CAP` for Diplomatic, Σ planet.populationCap
 *   for Population. Research has no cap (flows into research project per
 *   ECONOMY §4.4) so the "/cap" segment is omitted for it.
 * - `income` is the delta between the current resources and a rolling
 *   snapshot taken 10 ticks (one second of sim time) ago. Shows visible
 *   rate rather than per-tick fluctuation.
 *
 * DOM-based (not on-canvas) so text is crisp at any DPR and accessible to
 * screen readers.
 */

import { diplomaticCap } from '../game/balance-structures';
import { resourceCapsForEmpire } from '../game/planet-economy';
import type { GameState, ResourceKind } from '../state/types';

interface HudHandle {
  update(state: GameState): void;
  destroy(): void;
}

/** Display-order and per-resource formatting. Order locked by UI_UX playtest. */
const RESOURCE_META: ReadonlyArray<{
  key: ResourceKind;
  label: string;
  /** Whether the cell shows `/cap`. Research is the only uncapped resource. */
  showCap: boolean;
  valueFmt: (n: number) => string;
  /** Income is per-second (delta over 10 ticks). Fractional rates (e.g. pop) need decimals. */
  incomeFmt: (n: number) => string;
  /** v4M1.2 — native title tooltip text (UI_UX §1.1 + §6). */
  tooltip: string;
}> = [
  { key: 'credits',    label: 'Credits',    showCap: true,  valueFmt: fmtInt,   incomeFmt: fmtSignedInt,
    tooltip: 'Credits — primary currency. Spent on ships, structures, and warp-gate connections. Generated by population and trade.' },
  { key: 'minerals',   label: 'Minerals',   showCap: true,  valueFmt: fmtInt,   incomeFmt: fmtSignedInt,
    tooltip: 'Minerals — industrial resource. Spent on every build order alongside Credits. Mined by Extractors and Asteroid Mining Outposts.' },
  { key: 'fuel',       label: 'Fuel',       showCap: true,  valueFmt: fmtInt,   incomeFmt: fmtSignedInt,
    tooltip: 'Fuel — consumed per hyperdrive jump. Refined by Gas Giant Refineries and per-pop generation. Out-of-fuel ships cannot jump.' },
  { key: 'research',   label: 'Research',   showCap: false, valueFmt: fmtInt,   incomeFmt: fmtSignedInt,
    tooltip: 'Research — points spent on active tech. Pick a target in the Research panel; income flows directly into the project.' },
  { key: 'population', label: 'Pop',        showCap: true,  valueFmt: fmtFloat, incomeFmt: fmtSignedFloat,
    tooltip: 'Population — total across all owned planets. Drives per-pop resource generation. Capped by planet habitability + biome.' },
  { key: 'diplomatic', label: 'Diplomatic', showCap: true,  valueFmt: fmtInt,   incomeFmt: fmtSignedInt,
    tooltip: 'Diplomatic Influence — spent on Annex / Trade / Federation / Vassal of neutral planets via the diplomatic focus.' },
];

const INCOME_SAMPLE_INTERVAL_TICKS = 10; // one second at 10 Hz

function fmtInt(n: number): string {
  return Math.floor(n).toLocaleString();
}
function fmtFloat(n: number): string {
  return n.toFixed(1);
}
function fmtSignedInt(n: number): string {
  const v = Math.round(n);
  if (v === 0) return '±0';
  return v > 0 ? `+${v.toLocaleString()}` : v.toLocaleString();
}
function fmtSignedFloat(n: number): string {
  const rounded = Number(n.toFixed(2));
  if (rounded === 0) return '±0.00';
  return rounded > 0 ? `+${rounded.toFixed(2)}` : rounded.toFixed(2);
}

export function mountHud(parent: HTMLElement): HudHandle {
  const root = document.createElement('div');
  root.id = 'hud-topbar';
  Object.assign(root.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    padding: '0 16px',
    background: 'linear-gradient(180deg, rgba(6,10,20,0.95), rgba(6,10,20,0.7))',
    borderBottom: '1px solid rgba(80,110,160,0.35)',
    fontSize: '12px',
    color: '#d8e4ff',
    zIndex: '10',
    pointerEvents: 'none',
    userSelect: 'none',
  });

  const cells = new Map<string, { value: HTMLSpanElement; income: HTMLSpanElement }>();
  for (const meta of RESOURCE_META) {
    const cell = document.createElement('div');
    cell.style.display = 'flex';
    cell.style.gap = '6px';
    cell.style.alignItems = 'baseline';
    // v4M1.2 — opt this cell into pointer events so native `title` tooltips
    // fire on hover. The HUD root keeps pointer-events: none so the
    // bar-edge stays click-through for the canvas underneath; only the
    // interactive resource cells override that.
    cell.style.pointerEvents = 'auto';
    cell.title = meta.tooltip;
    const label = document.createElement('span');
    label.textContent = meta.label;
    label.style.color = '#7a8bb5';
    label.style.textTransform = 'uppercase';
    label.style.letterSpacing = '0.08em';
    label.style.fontSize = '10px';
    const value = document.createElement('span');
    value.textContent = '0';
    value.style.fontVariantNumeric = 'tabular-nums';
    value.style.fontWeight = '600';
    const income = document.createElement('span');
    income.textContent = '';
    income.style.fontVariantNumeric = 'tabular-nums';
    income.style.color = '#8ea0c8';
    income.style.fontSize = '11px';
    cell.appendChild(label);
    cell.appendChild(value);
    cell.appendChild(income);
    root.appendChild(cell);
    cells.set(meta.key, { value, income });
  }

  // Spacer pushes tick counter to the right.
  const spacer = document.createElement('div');
  spacer.style.flex = '1';
  root.appendChild(spacer);

  const tickCell = document.createElement('div');
  tickCell.style.color = '#6e80a8';
  tickCell.style.fontVariantNumeric = 'tabular-nums';
  tickCell.textContent = 'tick 0';
  tickCell.style.pointerEvents = 'auto';
  tickCell.title =
    'Current sim tick. The simulation runs at 10 Hz (10 ticks per second). Tick counter pauses when the game is paused.';
  root.appendChild(tickCell);

  parent.appendChild(root);

  // Rolling snapshot for income-per-second. Refreshed every
  // INCOME_SAMPLE_INTERVAL_TICKS ticks; between refreshes we keep showing
  // the cached delta so the reading is stable for ~1 second at a time.
  interface Snapshot {
    tick: number;
    credits: number;
    minerals: number;
    fuel: number;
    research: number;
    population: number;
    diplomatic: number;
  }
  let snapshot: Snapshot | null = null;
  let cachedIncome: Record<ResourceKind, number> = {
    credits: 0, minerals: 0, fuel: 0, research: 0, population: 0, diplomatic: 0,
  };

  return {
    update(state: GameState): void {
      const empire = state.empires.get(state.playerEmpireId);
      if (!empire) return;

      // Caps — storage resources come from planet-economy; Population sums
      // planet.populationCap across owned planets; Diplomatic uses the hard
      // cap constant; Research has no cap.
      const storageCaps = resourceCapsForEmpire(state, empire.id);
      let popCap = 0;
      for (const pid of empire.planetIds) {
        const planet = state.planets.get(pid);
        if (planet) popCap += planet.populationCap;
      }
      const caps: Record<ResourceKind, number> = {
        credits: storageCaps.credits,
        minerals: storageCaps.minerals,
        fuel: storageCaps.fuel,
        research: 0, // unused
        population: popCap,
        diplomatic: diplomaticCap(state.structuresData),
      };

      // Roll the income snapshot forward every 10 ticks. First update seeds
      // the snapshot (incomes stay at 0 until the next roll).
      if (snapshot === null) {
        snapshot = {
          tick: state.tick,
          credits: empire.resources.credits,
          minerals: empire.resources.minerals,
          fuel: empire.resources.fuel,
          research: empire.resources.research,
          population: empire.resources.population,
          diplomatic: empire.resources.diplomatic,
        };
      } else if (state.tick - snapshot.tick >= INCOME_SAMPLE_INTERVAL_TICKS) {
        // Delta over the interval = income per second.
        cachedIncome = {
          credits: empire.resources.credits - snapshot.credits,
          minerals: empire.resources.minerals - snapshot.minerals,
          fuel: empire.resources.fuel - snapshot.fuel,
          research: empire.resources.research - snapshot.research,
          population: empire.resources.population - snapshot.population,
          diplomatic: empire.resources.diplomatic - snapshot.diplomatic,
        };
        snapshot = {
          tick: state.tick,
          credits: empire.resources.credits,
          minerals: empire.resources.minerals,
          fuel: empire.resources.fuel,
          research: empire.resources.research,
          population: empire.resources.population,
          diplomatic: empire.resources.diplomatic,
        };
      }

      // Paint each cell.
      for (const meta of RESOURCE_META) {
        const cell = cells.get(meta.key);
        if (!cell) continue;
        // MFix1.1 — when research is actively draining into a target,
        // the bank reads 0 and the per-second income reads 0 (income
        // arrives, drain immediately consumes it). That's correct sim
        // behaviour but it presents to the player as "nothing's
        // happening." Replace the readout with project progress so
        // the income flow is visible. Bank balance + income readout
        // resume the moment activeResearch clears (tech complete or
        // cancelled).
        if (meta.key === 'research' && empire.activeResearch !== null) {
          const tech = state.techs.get(empire.activeResearch.techName);
          if (tech) {
            const acc = Math.floor(empire.activeResearch.rpAccumulated);
            cell.value.textContent = `${acc} / ${tech.researchCost}`;
            cell.income.textContent = `→ ${empire.activeResearch.techName}`;
            continue;
          }
        }
        const cur = empire.resources[meta.key];
        const valueStr = meta.showCap
          ? `${meta.valueFmt(cur)} / ${meta.valueFmt(caps[meta.key])}`
          : meta.valueFmt(cur);
        cell.value.textContent = valueStr;
        // v3M21.6 — Diplomatic focus chip. When the player has a
        // `diplomaticFocus` set (`DIPLOMACY.md §10`), the per-tick
        // Diplomatic income is rerouted to the focused planet's
        // Influence Map and bypasses the empire treasury (per
        // `advance.ts` step 3a). Surface the focus in the income
        // column the same way research projects surface their
        // target tech name above — same `→ <Name>` idiom.
        if (meta.key === 'diplomatic' && empire.diplomaticFocus !== null) {
          const focusPlanet = state.planets.get(empire.diplomaticFocus);
          if (focusPlanet) {
            cell.income.textContent = `→ ${focusPlanet.name}`;
            continue;
          }
        }
        cell.income.textContent = meta.incomeFmt(cachedIncome[meta.key]);
      }
      tickCell.textContent = `tick ${state.tick}`;
    },
    destroy(): void {
      root.remove();
    },
  };
}
